import moment from "moment";
import Employee from "@/model/Employee";
import Employment from "@/model/Employment";
import Wage from "@/model/Wage";
import {EmploymentType, WageType} from "@/enums";
import EmployeeService from "@/service/EmployeeService";
import MinimumWageService, {MinimumWageDto} from "@/service/MinimumWageService";

/**
 * Builds the employee the create dialogs start with: exported, paid an hourly wage at the minimum wage of the current
 * month and marginally employed, both from the current month on. The suggested employee number and, for a
 * registration, its personal data and the employment type it asks for still come from `GET /employees/default`.
 */
export async function createDefaultEmployee(registrationUuid?: string): Promise<Employee> {
  const currentMonth = moment().format("YYYY-MM-01");
  const [minimumWage, defaults] = await Promise.all([
    MinimumWageService.findMinimumWage(currentMonth),
    EmployeeService.getDefaultData(registrationUuid)
      .then(response => response.data)
  ]);

  const employee = new Employee();
  employee.employeeId = defaults.employeeId;
  employee.export = true;
  employee.wages = [createWage(currentMonth, minimumWage)];
  employee.employments = [createEmployment(currentMonth, EmploymentType.MIN)];

  if (registrationUuid) {
    employee.firstName = defaults.firstName;
    employee.lastName = defaults.lastName;
    employee.birthdate = defaults.birthdate;
    employee.email = defaults.email;
    employee.employments[0].employmentType = defaults.employments[0].employmentType;
  }

  return employee;
}

function createWage(validFrom: string, minimumWage: MinimumWageDto | null): Wage {
  // Every field is set, so that Vue tracks all of them
  return Object.assign(new Wage(), {
    wage: minimumWage ? minimumWage.amount : null,
    wageType: WageType.WAG,
    validFrom: validFrom,
    validTo: null,
    targetHours: null
  });
}

function createEmployment(validFrom: string, employmentType: EmploymentType): Employment {
  // Every field is set, so that Vue tracks all of them
  return Object.assign(new Employment(), {
    employmentType: employmentType,
    validFrom: validFrom,
    validTo: null
  });
}

/**
 * Sets the wage to the minimum wage valid from its new start, but only if that is another minimum wage than the one
 * valid on its previous start. A wage entered by hand is thereby kept as long as the minimum wage stays the same.
 */
export async function applyChangedMinimumWage(wage: Wage, previousValidFrom: string): Promise<void> {
  const validFrom = wage.validFrom;
  const [previousMinimumWage, minimumWage] = await Promise.all([
    MinimumWageService.findMinimumWage(moment(previousValidFrom).format("YYYY-MM-01")),
    MinimumWageService.findMinimumWage(moment(validFrom).format("YYYY-MM-01"))
  ]);

  // A later date change has overtaken this one
  if (wage.validFrom !== validFrom) {
    return;
  }
  if (minimumWage && minimumWage.id !== previousMinimumWage?.id) {
    wage.wage = minimumWage.amount;
  }
}
